import { ArrowDown } from '@lt/components/icons-react';
import {
  BuildingWithLoupe,
  NoFilterHotelResults,
} from '@lt/components/icons-react/userflow';
import React, { useEffect, useRef, useState } from 'react';
import HotelsLoadingPreview from 'src/components/Hotel/HotelsLoadingPreview';
import Hotels from 'src/routes/SearchPage/components/Hotels';
import { pluralizeHotels } from 'src/utils/pluralizeUtils';
import styled from 'styled-components';
import { useTranslation } from '@lt/localization';
import type { PropsFromRedux } from '.';
import FilterInput from '../FilterInput';
import type { SearchState } from './types';
import { TEST_ID_INITIAL_STATE_COMPONENT } from './constants';

const MobileHotelFilterPopup = (props: PropsFromRedux): JSX.Element => {
  const {
    applyFilters,
    clearFilters,
    setIsFilterOpened,
    hotelsCount,
    isFetching,
    isEmptyFiltration,
    visibleHotelsAmount,
  } = props;
  const [searchState, setSearchState] = useState<SearchState>('INITIAL');
  const [hotelName, setHotelName] = useState('');
  /**
   * isFirstFetching нужен для того, чтобы отображать скелетоны только для
   * первой ручки, дальше скелетоны отображать не нужно
   */
  const [isFirstFetching, setIsFirstFetching] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchState !== 'INITIAL')
      switch (true) {
        case isFetching && isFirstFetching:
          setSearchState('FIRST_FETCHING');
          setIsFirstFetching(false);
          break;
        case isFetching && !isFirstFetching:
          setSearchState('FETCHING');
          break;
        case Boolean(hotelsCount):
          setSearchState('FOUND_RESULTS');
          break;
        case isEmptyFiltration:
        default:
          setSearchState('NOT_FOUND');
          setIsFirstFetching(true);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelsCount, isFetching, isEmptyFiltration]);
  /**
   * Нужно для того, чтобы в мобильных браузерах
   * высота адаптировалась под браузерные topbar'ы
   */
  useEffect(() => {
    document.body.style.minHeight = '100dvh';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.minHeight = '100vh';
      document.body.style.overflow = 'auto';
    };
  }, []);
  const { t } = useTranslation();
  const welcomingMessage = t('filters.mobileHotelName.welcomingMessage');
  const fetchingText = t('filters.mobileHotelName.fetchingText');
  const foundText = t('filters.mobileHotelName.foundText');
  const notFoundText = t('filters.mobileHotelName.notFoundText');
  const placeholderText = t('filters.mobileHotelName.placeholder');
  const howToFindText = t('filters.mobileHotelName.howToFindText');

  const onChange = (newValue: string) => {
    setHotelName(newValue);
    applyFilters({ path: 'hotel_name', diff: { value: newValue } });
    if (isFirstFetching) setSearchState('FIRST_FETCHING');
    if (!newValue) {
      setSearchState('INITIAL');
      setIsFirstFetching(true);
    }
  };
  const handleGoBack = () => {
    setIsFilterOpened(false);
    clearFilters('hotel_name');
  };
  const handleWipeButtonClicked = () => {
    setSearchState('INITIAL');
    setHotelName('');
    setIsFirstFetching(true);
    if (inputRef.current !== null) inputRef.current.focus();
  };
  return (
    <>
      <StyledInputContainer>
        <IconContainer>
          <StyledArrowDown onClick={handleGoBack} />
        </IconContainer>
        <FilterInput
          isFilterPopup
          hotelName={hotelName}
          placeholderText={placeholderText}
          onChange={onChange}
          inputRef={inputRef}
          isFetching={
            searchState === 'FIRST_FETCHING' || searchState === 'FETCHING'
          }
          handleWipeButtonClicked={handleWipeButtonClicked}
        />
      </StyledInputContainer>

      <StyledSearchContainer>
        {searchState === 'INITIAL' && (
          <WelcomingMessageContainer
            data-testid={TEST_ID_INITIAL_STATE_COMPONENT}
          >
            <StyledBuildingWithLoupe />
            <MessageWrapper>{welcomingMessage}</MessageWrapper>
          </WelcomingMessageContainer>
        )}

        {searchState === 'FIRST_FETCHING' && (
          <>
            <FetchingText>{fetchingText}</FetchingText>
            <HotelsLoadingPreview
              visibleLimit={visibleHotelsAmount}
              penalizedHotelsVisibleLimit={0}
              smallCard
            />
          </>
        )}

        {searchState === 'FETCHING' && (
          <>
            <FetchingText>{fetchingText}</FetchingText>
            <StyledHotels cancelSkeletons mobileHotelFilter />
          </>
        )}

        {searchState === 'FOUND_RESULTS' && (
          <>
            <HotelsText>
              {foundText} {hotelsCount} {pluralizeHotels(hotelsCount)}
            </HotelsText>
            <StyledHotels mobileHotelFilter />
          </>
        )}

        {searchState === 'NOT_FOUND' && (
          <StyledNotFoundContainer>
            <StyledNoFilterHotelResults />
            <NotFoundText>{notFoundText}</NotFoundText>
            <HowToFindText>{howToFindText}</HowToFindText>
          </StyledNotFoundContainer>
        )}
      </StyledSearchContainer>
    </>
  );
};

const StyledInputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 12px 8px 4px 8px;
  position: sticky;
  top: 0;
  z-index: 5;
  background: white;
`;

const StyledHotels = styled(Hotels)`
  margin: 0px;
  &:not(:last-child) {
    padding-bottom: 8px;
  }
  background: #f3f3f3;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
`;

const StyledArrowDown = styled(ArrowDown)`
  transform: rotate(-90deg);
  & > path {
    fill: #007aff;
  }
  width: 24px;
  height: 24px;
`;

const StyledSearchContainer = styled.div`
  height: 100%;
`;

const WelcomingMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: absolute;
  left: 15px;
  top: 219px;
  right: 15px;
`;

const StyledBuildingWithLoupe = styled(BuildingWithLoupe)`
  width: 45px;
  height: 33px;
`;

const MessageWrapper = styled.div`
  color: #919499;
  text-align: center;
  font-family: Backpack;
  font-size: 15px;
  font-weight: 400;
  width: 290px;
`;

const FetchingText = styled.div`
  padding: 12px 8px;
  color: #919499;
  font-size: 14px;
  font-weight: 400;
`;

const HotelsText = styled.div`
  padding: 12px 8px;
  color: #4d4d4d;
  font-size: 14px;
  font-weight: 400;
`;

const StyledNotFoundContainer = styled.div`
  display: flex;
  width: 100%;
  height: 275px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  position: absolute;
  top: 88px;
  right: 8px;
  left: 0px;
`;

const NotFoundText = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
`;
const HowToFindText = styled.div`
  width: 290px;
  color: #4d4d4d;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
`;

const StyledNoFilterHotelResults = styled(NoFilterHotelResults)`
  width: 166px;
  height: 98px;
`;

export default MobileHotelFilterPopup;
